import { useState, useCallback, useEffect, useRef } from "react"
import {
  fetchPipelines, fetchDeals, fetchDeal,
  changeDealStage, winDeal, loseDeal, deleteDeal,
} from "../api/dealsApi"

// ── usePipelines ─────────────────────────────────────────────────────────────
export function usePipelines() {
  const [pipelines, setPipelines] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    fetchPipelines()
      .then((p) => { if (active) setPipelines(p) })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  return { pipelines, loading }
}

// ── useDeals ─────────────────────────────────────────────────────────────────
export function useDeals() {
  const [data, setData] = useState(null) // { deals, meta }
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const abortRef = useRef(null)

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    setLoading(true)
    setError(null)
    try {
      setData(await fetchDeals(filters))
    } catch (err) {
      if (err.name !== "AbortError") setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  return { data, loading, error, fetch, refresh: fetch }
}

// ── useDeal (detail) ─────────────────────────────────────────────────────────
export function useDeal(id) {
  const [deal, setDeal] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    fetchDeal(id)
      .then(setDeal)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => { load() }, [load])

  return { deal, setDeal, loading, reload: load }
}

// ── Mutations ────────────────────────────────────────────────────────────────
function useMutation(fn) {
  const [isPending, setPending] = useState(false)
  const mutate = useCallback(async (...args) => {
    setPending(true)
    try {
      return await fn(...args)
    } catch (err) {
      const msg = err.response?.data?.errors || [err.message || "Something went wrong"]
      throw msg
    } finally {
      setPending(false)
    }
  }, [fn])
  return { mutate, isPending }
}

export const useChangeStage = () => useMutation(changeDealStage)
export const useWinDeal     = () => useMutation(winDeal)
export const useLoseDeal    = () => useMutation(loseDeal)
export const useDeleteDeal  = () => useMutation(deleteDeal)
